import type { ChangeEvent, ComponentProps } from 'react';
import React, { useCallback } from 'react';
import styled from '@emotion/styled';
import { ReclamationAccessPolicy } from 'graphql-ts-types';
import {
  alignItems,
  color,
  display,
  fontSize,
  fontWeight,
  height,
  lineHeight,
  maxHeight,
  maxWidth,
  space,
  width,
} from 'styled-system';

import Icon from 'common/components/Icon';
import Input from 'common/components/Input';
import { LinkWithIcon as BaseLink } from 'common/components/Link';
import ReclamationAlert from 'common/components/Reclamation/ReclamationAlert';
import { Text } from 'common/components/Text';
import { useOpenDrawer } from 'common/hooks/drawer';
import { useLocalization } from 'common/providers/LocaleProvider';
import { RauchNumberDrawer } from './RauchNumberDrawer';

interface PartNumberAdditionProps {
  value: string;
  areInputsDisabled: boolean;
  shouldValidate: boolean;
  accessPolicy: ReclamationAccessPolicy;
  onChange: (value: string) => void;
}

const ICON_SIZE = ['30px', '20px'];

const UnstyledContainer = styled.div<any>`
  ${space};
`;

const Container = (props: ComponentProps<typeof UnstyledContainer>) => (
  <UnstyledContainer mb="sp_20" {...props} />
);

const UnstyledLabel = styled.label<any>`
  ${display};
  ${color};
  ${fontSize};
  ${fontWeight};
  ${lineHeight};
  ${space};
`;

const Label = (props: ComponentProps<typeof UnstyledLabel>) => (
  <UnstyledLabel
    display="block"
    color="sys.neutral.text.strong"
    fontSize="fs_16"
    fontWeight="bold"
    lineHeight="lh_20"
    mb="sp_4"
    {...props}
  />
);

const UnstyledDescription = styled(Text)``;

const Description = (props: ComponentProps<typeof UnstyledDescription>) => (
  <UnstyledDescription
    fontSize="fs_16"
    mb="sp_16"
    color="sys.neutral.text.default"
    lineHeight="lh_20"
    {...props}
  />
);

const UnstyledLink = styled(BaseLink)<any>`
  ${display};
  ${alignItems};
  ${color};
`;

const Link = (props: ComponentProps<typeof UnstyledLink>) => (
  <UnstyledLink
    display="flex"
    alignItems="center"
    color="sys.neutral.text.default"
    fontSize={['fs_16', null, null, 'fs_18']}
    lineHeight={['lh_20', null, null, 'lh_24']}
    {...props}
  />
);

const UnstyledArrowRight = styled(Icon)<any>`
  ${width};
  ${height};
  ${maxWidth};
  ${maxHeight};
`;

const ArrowRight = (props: ComponentProps<typeof UnstyledArrowRight>) => (
  <UnstyledArrowRight
    width={ICON_SIZE}
    height={ICON_SIZE}
    maxWidth={ICON_SIZE}
    maxHeight={ICON_SIZE}
    name="arrowRight"
    {...props}
  />
);

const UnstyledStyledInput = styled(Input)``;

const StyledInput = (props: ComponentProps<typeof UnstyledStyledInput>) => (
  <UnstyledStyledInput px="sp_12" mb="sp_0" {...props} />
);

const VendorOrderId = ({
  value,
  areInputsDisabled,
  accessPolicy,
  shouldValidate,
  onChange,
}: PartNumberAdditionProps) => {
  const { t } = useLocalization();
  const onInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value ?? '');
  }, []);
  const openVendorIdDrawer = useOpenDrawer({
    content: <RauchNumberDrawer />,
    title: t('h24_rauch_number_drawer_title'),
    incrementZIndex: true,
  });

  return (
    <Container>
      <Label htmlFor="add-part-number-input">
        {t('h24_rauch_number_title')}
        {accessPolicy !== ReclamationAccessPolicy.Required ? ` (${t('h24_optional')})` : ''}
      </Label>
      {shouldValidate && value === '' && <ReclamationAlert message={t('h24_part_forced_error')} />}
      <Description>{t('h24_rauch_number_description')}</Description>

      <StyledInput
        type="text"
        name="add-part-number-input"
        id="add-part-number-input"
        placeholder={t('h24_rauch_number_input_placeholder')}
        value={value}
        disabled={areInputsDisabled}
        onChange={onInputChange}
        autoComplete="off"
      />

      <Link onClick={openVendorIdDrawer}>
        {t('h24_rauch_number_drawer_title')}
        <ArrowRight />
      </Link>
    </Container>
  );
};

export default VendorOrderId;
